/**
 * PaymentMethodManager
 * Owns the active payment method: resolving it from settings at init,
 * applying changes, and republishing state that depends on it.
 *
 * This class is not exposed to the consuming application.
 */

import { createLogger } from '@anddone/ajs-logger';
import type { PaymentMethod, CommandDispatcher } from '@anddone/ajs-util';
import {
  CHANNEL_TYPE_PAYMENT_METHOD,
  DEFAULT_PAYMENT_METHOD,
  PAYMENT_METHOD_CHANNEL_TYPE,
} from '@anddone/ajs-util';
import type { Events } from '../Events';
import type { FragmentRegistry } from '../fragment-registry/FragmentRegistry';
import type { SettingsState } from './SettingsState';
import type { FeesAndTotalsManager } from './FeesAndTotalsManager';
import type { PaymentAuthorizationManager } from './PaymentAuthorizationManager';

const logger = createLogger('SDK', 'PaymentMethodManager');

export class PaymentMethodManager {
  #current: PaymentMethod = DEFAULT_PAYMENT_METHOD;

  constructor(
    private readonly settings: SettingsState,
    private readonly dispatcher: CommandDispatcher,
    private readonly events: Events,
    private readonly fragmentRegistry: FragmentRegistry,
    private readonly fees: FeesAndTotalsManager,
    private readonly paymentAuthorization: PaymentAuthorizationManager,
  ) {}

  public get current(): PaymentMethod {
    return this.#current;
  }

  /**
   * Switch the active payment method.
   *
   * Syncs the registry, tells payment fragments, notifies the orchestrator
   * fragment and the consumer, then republishes fees and payment authorization.
   *
   * @param paymentMethod - The payment method to activate
   * @returns true if the method changed, false if it was already active
   */
  public update(paymentMethod: PaymentMethod): boolean {
    logger.info('update-payment-method', `Updating payment method.`, { paymentMethod });

    if (paymentMethod === this.#current) {
      logger.debug(
        'update-payment-method',
        `Payment method is already set to ${paymentMethod}. No update needed.`,
      );
      return false;
    }

    this.#current = paymentMethod;
    this.fragmentRegistry.setPaymentMethod(paymentMethod);
    logger.info('update-payment-method', `Payment method updated to ${paymentMethod}.`);

    this.dispatcher.dispatch('payment-fragments', 'set-payment-method', { paymentMethod });

    this.dispatcher.logToOrchestrator(`Payment method updated to: ${paymentMethod}`);
    this.dispatcher.refreshOrchestratorStatus();

    this.events.emit('payment-method-change', { paymentMethod });

    this.publishDerivedState();

    // TODO: add validation to ensure the payment intent includes the payment method passed. Return false if not valid.
    return true;
  }

  /**
   * Pick the initial payment method from settings.
   *
   * If allowedTransactionTypes does not include the channel type for
   * DEFAULT_PAYMENT_METHOD, switch to the first listed channel type that maps
   * to a known PaymentMethod. update() republishes derived state in that case.
   *
   * @returns true if an override was applied, false if the default was kept
   */
  public resolveInitial(): boolean {
    const settings = this.settings.current;

    if (!settings || settings.allowedTransactionTypes.length === 0) {
      logger.debug(
        'resolve-initial-payment-method',
        'Skipped — settings not available or allowedTransactionTypes is empty.',
      );
      return false;
    }

    const defaultChannelType = PAYMENT_METHOD_CHANNEL_TYPE[DEFAULT_PAYMENT_METHOD];

    if (this.settings.isChannelAllowed(defaultChannelType)) {
      logger.debug(
        'resolve-initial-payment-method',
        `Default payment method "${DEFAULT_PAYMENT_METHOD}" is supported — no override needed.`,
      );
      return false;
    }

    for (const { channelType } of settings.allowedTransactionTypes) {
      const paymentMethod = CHANNEL_TYPE_PAYMENT_METHOD.get(channelType);

      if (paymentMethod) {
        logger.info(
          'resolve-initial-payment-method',
          `Default payment method "${DEFAULT_PAYMENT_METHOD}" is not in allowedTransactionTypes — overriding to "${paymentMethod}".`,
          { defaultChannelType, channelType },
        );
        return this.update(paymentMethod);
      }
    }

    logger.warn(
      'resolve-initial-payment-method',
      `No known payment method found in allowedTransactionTypes — keeping default "${DEFAULT_PAYMENT_METHOD}".`,
      { allowedTransactionTypes: settings.allowedTransactionTypes },
    );
    return false;
  }

  /**
   * Republish fees and payment authorization for the active payment method.
   *
   * @returns void
   */
  public publishDerivedState(): void {
    this.fees.publish(this.#current);
    this.paymentAuthorization.publish(this.#current);
  }
}

Object.freeze(PaymentMethodManager.prototype);
